import { type ComponentProps, type FC } from 'react';
import moment from 'moment-timezone';
import classNames from 'classnames/bind';
import { FontAwesomeIcon, Colors, PremierButton, Row } from '@rivallapp/volosports-components';

import { faCalendarDays } from '@fortawesome/pro-solid-svg-icons/faCalendarDays';
import { faLocationDot } from '@fortawesome/pro-solid-svg-icons/faLocationDot';
import { faClock } from '@fortawesome/pro-solid-svg-icons/faClock';
import { faSignal } from '@fortawesome/pro-solid-svg-icons/faSignal';
import { faMap } from '@fortawesome/pro-solid-svg-icons/faMap';

import styles from './styles.module.scss';
import FormatTimeFrame from '../../tools/helpers';
import { commaSeparatedList } from '../../../../../utils';
import type {
  GetGameDetailsData,
  GetLeagueDetailsData,
} from '../../../../../hooks/useRegistrationData/QUERIES_AND_MUTATIONS';
import { MemberExclusiveButton } from '../../../../../components';

const cx = classNames.bind(styles);

type FontAwesomeIconProps = ComponentProps<typeof FontAwesomeIcon>;

export const ProgramDetailIcon = (props: FontAwesomeIconProps) => (
  <FontAwesomeIcon color={Colors.white} size="md" mr={1} {...props} />
);

type DetailItemProps = ComponentProps<typeof Row> & Partial<Pick<FontAwesomeIconProps, 'icon'>>;

const DetailItem: FC<DetailItemProps> = ({ icon, children, ...props }) => (
  <Row alignItems="center" p={2} {...props}>
    {icon && <ProgramDetailIcon icon={icon} />}
    {children}
  </Row>
);

type Props = {
  game?: GetGameDetailsData['game'];
  league?: GetLeagueDetailsData['league'];
};

const ProgramDetailItems = (props: Props) => {
  const { game, league } = props;

  const start = game ? game.startTimeStr : league?.start_time_estimate;
  const end = game ? game.endTimeStr : league?.end_time_estimate;
  const timezone = (game ? game.location?.timezone : league?.venue?.timezone) ?? '';
  const formattedTime = FormatTimeFrame(start, end, timezone);

  const isPremier = game ? game.league?.is_premier : league?.is_premier;

  const isVoloPassExclusive = game
    ? game.league?.is_volo_pass_exclusive
    : league?.is_volo_pass_exclusive;

  const formattedDate = game
    ? moment(game?.start_time).tz(timezone).format('ddd, MMMM D')
    : moment(league?.startDateStr, 'YY/MM/DD').format('ddd, MMMM D');

  const leagueDivisionDescriptions = league?.divisionDescriptions
    ? commaSeparatedList(league.divisionDescriptions, { joiner: ',' })
    : 'Any level';
  const gameDivisionDescriptions = game?.league?.divisionDescriptions
    ? commaSeparatedList(game.league.divisionDescriptions, { joiner: ',' })
    : 'Any level';

  const formattedAddress = game
    ? game.league?.venue?.formatted_address
    : league?.venue?.formatted_address;

  const venueName = game ? game.location?.name : league?.venue?.name;
  const neighborhoodName = game ? game.location?.neighborhood : league?.venue?.neighborhood;

  const field_name = game?.field_name;

  const items: DetailItemProps[] = [
    { key: 'date', icon: faCalendarDays, children: formattedDate },
    {
      key: 'neighborhood',
      icon: faMap,
      children: neighborhoodName,
    },
    { key: 'time', icon: faClock, children: formattedTime },
    {
      key: 'venue',
      icon: faLocationDot,
      children: (
        <a
          href={`https://www.google.com/maps/dir//${
            formattedAddress?.replace(/,/g, '').replace(/ /g, '+') ?? '#'
          }`}
          target="_blank"
          rel="noopener noreferrer"
        >
          {venueName}
        </a>
      ),
    },
    {
      key: 'division',
      icon: faSignal,
      children: game ? gameDivisionDescriptions : leagueDivisionDescriptions,
    },
  ];

  if (field_name) items.push({ key: 'field', icon: faLocationDot, children: field_name });

  if (isVoloPassExclusive)
    items.push({
      key: 'member',
      children: <MemberExclusiveButton _iconProps={{ color: Colors.white }} />,
    });

  if (isPremier)
    items.push({
      key: 'premier',
      children: (
        <PremierButton
          _iconProps={{ color: Colors.white }}
          _buttonProps={{
            px: 1,
            height: '20px',
            _text: { fontSize: 14 },
          }}
        />
      ),
    });

  return (
    <div className={cx('program-detail-item-container')}>
      {items.map(item => (
        <DetailItem {...item} />
      ))}
    </div>
  );
};

export default ProgramDetailItems;
